import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, BarChart, Bar, Cell, LabelList
} from "recharts";
import { motion } from "framer-motion";
import { TrendingDown, Activity, ArrowDownRight, Info, Filter } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const formatDateFR = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' });
};

const getMonthLabelFR = (monthKey) => {
  // monthKey format "YYYY-MM"
  if (!monthKey) return '';
  const [year, month] = monthKey.split('-');
  const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1);
  const label = date.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit', timeZone: 'Europe/Paris' });
  // Nettoyer le point d'abréviation ("janv." → "Janv") et capitaliser
  return label.replace('.', '').replace(/^./, c => c.toUpperCase());
};

const STATUT_LABELS = {
  resilier: { label: "Résilié", color: "#dc2626", bg: "bg-red-100", text: "text-red-800", border: "border-red-300" },
  resiliation_en_cours: { label: "Résil. en cours", color: "#f97316", bg: "bg-orange-100", text: "text-orange-800", border: "border-orange-300" },
  fin_contrat: { label: "Fin de contrat", color: "#6b7280", bg: "bg-gray-100", text: "text-gray-800", border: "border-gray-300" },
};

const CustomTooltip = ({ active, payload, metricLabel = 'MRR' }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm max-w-xs">
      <p className="font-semibold text-foreground capitalize">{d.month_label}</p>
      <p className="text-muted-foreground text-xs mb-2">
        {d.is_past ? 'Historique' : d.is_current ? 'Mois en cours' : 'Projection'}
      </p>
      <div className="space-y-1">
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">{metricLabel}</span>
          <span className="font-mono font-bold">{formatCurrency(d.mrr)}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">Contrats actifs</span>
          <span className="font-mono">{d.active_count}</span>
        </div>
        {d.cancellations_impact > 0 && (
          <div className="pt-2 mt-2 border-t space-y-1">
            <p className="text-xs font-medium text-red-600">
              Sorties ce mois : −{formatCurrency(d.cancellations_impact)}
            </p>
            {d.impact_resilier > 0 && (
              <p className="text-xs text-red-600 flex justify-between">
                <span>• Résiliés</span><span className="font-mono">−{formatCurrency(d.impact_resilier)}</span>
              </p>
            )}
            {d.impact_pending > 0 && (
              <p className="text-xs text-orange-600 flex justify-between">
                <span>• Résil. en cours</span><span className="font-mono">−{formatCurrency(d.impact_pending)}</span>
              </p>
            )}
            {d.impact_fin_contrat > 0 && (
              <p className="text-xs text-gray-600 flex justify-between">
                <span>• Fin de contrat</span><span className="font-mono">−{formatCurrency(d.impact_fin_contrat)}</span>
              </p>
            )}
            <div className="pt-1 border-t border-gray-100">
              {d.cancellations.slice(0, 3).map((c, i) => {
                const s = STATUT_LABELS[c.statut] || STATUT_LABELS.fin_contrat;
                return (
                  <p key={i} className="text-xs truncate" style={{ color: s.color }}>
                    • {c.societe} <span className="font-mono">−{formatCurrency(c.montant)}</span>
                  </p>
                );
              })}
              {d.cancellations.length > 3 && (
                <p className="text-xs text-muted-foreground italic">
                  +{d.cancellations.length - 3} autre(s)...
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default function ContratsEvolution() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [includePending, setIncludePending] = useState(true);
  const [horizonPreset, setHorizonPreset] = useState("12"); // "12" | "24" | "36" | "60" | "auto"

  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      setLoading(true);
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const isAuto = horizonPreset === "auto";
        const res = await axios.get(`${API}/contracts/stats/mrr-timeline`, {
          headers,
          params: {
            months_back: 12,
            months_forward: isAuto ? 12 : parseInt(horizonPreset, 10),
            filter_type: filterType,
            include_pending: includePending,
            horizon_mode: isAuto ? "auto" : "manual",
          },
        });
        // Normalise les labels mensuels en français (le backend renvoie l'anglais via strftime)
        const raw = res.data;
        const timelineFR = (raw.timeline || []).map(t => ({
          ...t,
          month_label: getMonthLabelFR(t.month),
          cancellations: (t.cancellations || []).map(c => ({ ...c })),
        }));
        const upcomingFR = (raw.upcoming_cancellations || []).map(c => ({
          ...c,
          month_label: getMonthLabelFR(c.month),
        }));
        setData({ ...raw, timeline: timelineFR, upcoming_cancellations: upcomingFR });
      } catch (error) {
        console.error("Error fetching MRR timeline:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, filterType, includePending, horizonPreset]);

  const currentMonthKey = useMemo(() => {
    const t = data?.timeline?.find(m => m.is_current);
    return t?.month;
  }, [data]);

  const waterfallData = useMemo(() => {
    if (!data?.timeline) return [];
    // Cumule les résiliations futures mois par mois → data waterfall
    const futureTimeline = data.timeline.filter(t => t.is_future && t.cancellations_impact > 0);
    return futureTimeline.flatMap(t =>
      t.cancellations.map(c => ({
        label: `${c.societe.substring(0, 20)}`,
        societe: c.societe,
        montant: -c.montant,
        month_label: t.month_label,
        date_effet: c.date_effet,
        statut: c.statut,
        id: c.id,
      }))
    );
  }, [data]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 skeleton rounded"></div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-24 skeleton rounded-lg"></div>)}
        </div>
        <div className="h-96 skeleton rounded-lg"></div>
      </div>
    );
  }

  const kpis = data?.kpis || {};
  const timeline = data?.timeline || [];
  const upcoming = data?.upcoming_cancellations || [];

  // Libellé de la métrique selon le filtre actif
  const metricLabel =
    filterType === 'gestion_interne' ? 'Abo. Gestion Interne'
    : filterType === 'finance' ? 'Abo. Finance'
    : 'MRR';

  const renderDelta = (delta, pct) => {
    if (!delta || delta === 0) {
      return <span className="text-xs text-muted-foreground">Stable</span>;
    }
    const isNeg = delta < 0;
    return (
      <span className={`text-xs font-medium flex items-center gap-1 ${isNeg ? 'text-red-600' : 'text-green-600'}`}>
        {isNeg ? <ArrowDownRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3 rotate-180" />}
        {formatCurrency(delta)} ({pct > 0 ? '+' : ''}{pct}%)
      </span>
    );
  };

  return (
    <div className="space-y-6" data-testid="contrats-evolution">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Évolution du Portefeuille</h1>
          <p className="text-muted-foreground text-sm">
            Historique + Projection — impact des sorties (résiliations + fins de contrat) à leur date d&apos;effet
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={horizonPreset} onValueChange={setHorizonPreset}>
            <SelectTrigger className="w-40" data-testid="horizon-select">
              <SelectValue placeholder="Horizon" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="12">1 an</SelectItem>
              <SelectItem value="24">2 ans</SelectItem>
              <SelectItem value="36">3 ans</SelectItem>
              <SelectItem value="60">5 ans</SelectItem>
              <SelectItem value="auto">
                Auto ({data?.max_horizon_suggested ? `${data.max_horizon_suggested} mois` : '...'})
              </SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-48" data-testid="filter-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tout le portefeuille</SelectItem>
              <SelectItem value="gestion_interne">Gestion Interne</SelectItem>
              <SelectItem value="finance">Finance (Leasing)</SelectItem>
            </SelectContent>
          </Select>
          <div className={`flex items-center gap-2 px-3 py-2 border rounded-md bg-white transition ${filterType === 'gestion_interne' ? 'border-emerald-400 bg-emerald-50' : ''}`}>
            <Switch
              id="view-gestion-interne"
              checked={filterType === 'gestion_interne'}
              onCheckedChange={(checked) => setFilterType(checked ? 'gestion_interne' : 'all')}
              data-testid="view-gestion-interne-switch"
            />
            <Label htmlFor="view-gestion-interne" className="text-xs cursor-pointer whitespace-nowrap">
              Vue Gestion Interne
            </Label>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 border rounded-md bg-white">
            <Switch
              id="include-pending"
              checked={includePending}
              onCheckedChange={setIncludePending}
              data-testid="include-pending-switch"
            />
            <Label htmlFor="include-pending" className="text-xs cursor-pointer">
              Inclure résil. en cours
            </Label>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-white border-2 border-blue-200" data-testid="kpi-current">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full bg-blue-500"></div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Aujourd&apos;hui</p>
            </div>
            <p className="text-2xl font-bold text-foreground">{formatCurrency(kpis.current_mrr)}</p>
            <p className="text-xs text-muted-foreground">/mois</p>
          </CardContent>
        </Card>

        <Card className="bg-white border" data-testid="kpi-3m">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Dans 3 mois</p>
            <p className="text-2xl font-bold text-foreground">{formatCurrency(kpis.mrr_3_months)}</p>
            {renderDelta(kpis.delta_3_months, kpis.delta_3_pct)}
          </CardContent>
        </Card>

        <Card className="bg-white border" data-testid="kpi-6m">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Dans 6 mois</p>
            <p className="text-2xl font-bold text-foreground">{formatCurrency(kpis.mrr_6_months)}</p>
            {renderDelta(kpis.delta_6_months, kpis.delta_6_pct)}
          </CardContent>
        </Card>

        <Card className="bg-white border" data-testid="kpi-12m">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Dans 12 mois</p>
            <p className="text-2xl font-bold text-foreground">{formatCurrency(kpis.mrr_12_months)}</p>
            {renderDelta(kpis.delta_12_months, kpis.delta_12_pct)}
          </CardContent>
        </Card>
      </div>

      {/* Timeline chart */}
      <Card className="bg-white border">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Activity className="w-4 h-4 text-blue-600" />
            {metricLabel} — 12 mois passés + {data?.months_forward_used || 12} mois futurs
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            <span className="inline-block w-3 h-3 bg-blue-500 rounded-sm mr-1 align-middle"></span>
            Historique
            <span className="inline-block w-3 h-3 bg-blue-300 rounded-sm ml-4 mr-1 align-middle"></span>
            Projection
            <span className="ml-4">— chaque baisse = sortie de contrat (résiliation ou fin naturelle) à sa date d&apos;effet</span>
          </p>
        </CardHeader>
        <CardContent>
          {timeline.length > 0 ? (
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={timeline} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                  <defs>
                    <linearGradient id="colorPast" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.2}/>
                    </linearGradient>
                    <linearGradient id="colorFuture" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#93c5fd" stopOpacity={0.7}/>
                      <stop offset="95%" stopColor="#93c5fd" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month_label" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}k€`} tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip metricLabel={metricLabel} />} />

                  {/* Zone historique (opacité pleine) */}
                  <Area
                    type="stepAfter"
                    dataKey={(d) => (d.is_past || d.is_current ? d.mrr : null)}
                    stroke="#3b82f6"
                    strokeWidth={2}
                    fill="url(#colorPast)"
                    connectNulls={false}
                    isAnimationActive={false}
                    name="Historique"
                  />
                  {/* Zone projection (hachurée + plus claire) */}
                  <Area
                    type="stepAfter"
                    dataKey={(d) => (d.is_future || d.is_current ? d.mrr : null)}
                    stroke="#60a5fa"
                    strokeWidth={2}
                    strokeDasharray="6 4"
                    fill="url(#colorFuture)"
                    connectNulls={false}
                    isAnimationActive={false}
                    name="Projection"
                  />

                  {/* Ligne verticale "aujourd'hui" */}
                  {currentMonthKey && (
                    <ReferenceLine
                      x={timeline.find(t => t.is_current)?.month_label}
                      stroke="#6b7280"
                      strokeDasharray="4 4"
                      label={{ value: "Aujourd'hui", position: "top", fill: "#6b7280", fontSize: 11 }}
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-96 flex items-center justify-center text-muted-foreground">
              Aucune donnée disponible
            </div>
          )}
        </CardContent>
      </Card>

      {/* Waterfall - impact des sorties */}
      {waterfallData.length > 0 && (
        <Card className="bg-white border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-red-600" />
              Impact des sorties à venir
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Décomposition contrat par contrat des baisses de {metricLabel} sur la période affichée (résiliations + fins naturelles)
            </p>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={waterfallData} margin={{ top: 20, right: 20, left: 10, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 10 }}
                    angle={-25}
                    textAnchor="end"
                    height={70}
                  />
                  <YAxis tickFormatter={(v) => `${v}€`} tick={{ fontSize: 11 }} />
                  <Tooltip
                    formatter={(value, name, props) => {
                      const s = STATUT_LABELS[props.payload.statut] || STATUT_LABELS.fin_contrat;
                      return [
                        `${formatCurrency(Math.abs(value))}/mois — ${s.label}`,
                        props.payload.societe,
                      ];
                    }}
                    labelFormatter={() => ''}
                    contentStyle={{ borderRadius: '8px', fontSize: 12 }}
                  />
                  <Bar dataKey="montant" radius={[0, 0, 4, 4]}>
                    {waterfallData.map((entry, i) => {
                      const s = STATUT_LABELS[entry.statut] || STATUT_LABELS.fin_contrat;
                      return <Cell key={`cell-${i}`} fill={s.color} cursor="pointer" />;
                    })}
                    <LabelList
                      dataKey="montant"
                      position="bottom"
                      formatter={(v) => formatCurrency(Math.abs(v))}
                      style={{ fontSize: 10, fill: '#6b7280' }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground flex-wrap">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-sm bg-red-600"></div>
                <span>Résilié (validé)</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-sm bg-orange-500"></div>
                <span>Résiliation en cours</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-sm bg-gray-500"></div>
                <span>Fin de contrat (échéance naturelle)</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detail table - impact par mois */}
      <Card className="bg-white border">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Impact mensuel détaillé
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left py-2 px-3 font-medium text-muted-foreground">Mois</th>
                  <th className="text-right py-2 px-3 font-medium text-muted-foreground">{metricLabel}</th>
                  <th className="text-right py-2 px-3 font-medium text-muted-foreground">Δ vs mois précédent</th>
                  <th className="text-right py-2 px-3 font-medium text-muted-foreground">Sorties</th>
                  <th className="text-left py-2 px-3 font-medium text-muted-foreground">Détail sorties</th>
                </tr>
              </thead>
              <tbody>
                {timeline.map((t, i) => {
                  const prev = i > 0 ? timeline[i - 1] : null;
                  const delta = prev ? t.mrr - prev.mrr : 0;
                  const isDrop = delta < -0.01;
                  return (
                    <tr
                      key={t.month}
                      className={`border-b hover:bg-gray-50 ${t.is_current ? 'bg-blue-50/50 font-medium' : ''}`}
                    >
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2">
                          <span className="capitalize">{t.month_label}</span>
                          {t.is_current && <Badge className="text-[10px] bg-blue-500">Actuel</Badge>}
                          {t.is_past && <span className="text-xs text-muted-foreground">passé</span>}
                        </div>
                      </td>
                      <td className="text-right py-2 px-3 font-mono">{formatCurrency(t.mrr)}</td>
                      <td className={`text-right py-2 px-3 font-mono text-xs ${isDrop ? 'text-red-600 font-bold' : delta > 0.01 ? 'text-green-600' : 'text-muted-foreground'}`}>
                        {i === 0 ? '—' : `${delta > 0 ? '+' : ''}${formatCurrency(delta)}`}
                      </td>
                      <td className="text-right py-2 px-3 font-mono text-xs">
                        {t.cancellations_impact > 0 ? (
                          <div className="flex flex-col items-end">
                            <span className="text-red-600 font-bold">−{formatCurrency(t.cancellations_impact)}</span>
                            <div className="flex gap-2 text-[10px]">
                              {t.impact_resilier > 0 && <span className="text-red-600">R:{formatCurrency(t.impact_resilier)}</span>}
                              {t.impact_pending > 0 && <span className="text-orange-600">EC:{formatCurrency(t.impact_pending)}</span>}
                              {t.impact_fin_contrat > 0 && <span className="text-gray-600">FC:{formatCurrency(t.impact_fin_contrat)}</span>}
                            </div>
                          </div>
                        ) : '—'}
                      </td>
                      <td className="py-2 px-3 text-xs text-muted-foreground">
                        {t.cancellations.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {t.cancellations.slice(0, 3).map((c, ci) => {
                              const s = STATUT_LABELS[c.statut] || STATUT_LABELS.fin_contrat;
                              return (
                                <button
                                  key={ci}
                                  onClick={() => c.id && navigate(`/contrats/${c.id}`)}
                                  className="hover:underline flex items-center gap-1"
                                  style={{ color: s.color }}
                                  title={s.label}
                                  data-testid={`cancellation-link-${c.id}`}
                                >
                                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: s.color }}></span>
                                  {c.societe}
                                </button>
                              );
                            })}
                            {t.cancellations.length > 3 && (
                              <span>+{t.cancellations.length - 3}</span>
                            )}
                          </div>
                        ) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="text-[10px] text-muted-foreground mt-2 italic">
              Légende: R = Résilié · EC = Résiliation en cours · FC = Fin de contrat
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Upcoming exits list */}
      {upcoming.length > 0 && (
        <Card className="bg-white border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">
              Sorties à venir ({upcoming.length})
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Résiliations + fins naturelles de contrat sur la période affichée
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {upcoming.map((c, i) => {
                const s = STATUT_LABELS[c.statut] || STATUT_LABELS.fin_contrat;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: Math.min(i * 0.02, 0.5) }}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer hover:shadow-sm transition ${s.bg.replace('100', '50')} ${s.border.replace('300', '200')}`}
                    onClick={() => c.id && navigate(`/contrats/${c.id}`)}
                    data-testid={`upcoming-cancellation-${c.id}`}
                  >
                    <div>
                      <p className="font-medium text-foreground">{c.societe}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge
                          variant="outline"
                          className={`text-[10px] ${s.bg} ${s.text} ${s.border}`}
                        >
                          {s.label}
                        </Badge>
                        <span className="text-xs text-muted-foreground">{c.produit}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-mono font-bold" style={{ color: s.color }}>−{formatCurrency(c.montant)}/mois</p>
                      <p className="text-xs text-muted-foreground">Effet: {formatDateFR(c.date_effet)}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info footer */}
      <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-900">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <p>
          <strong>Méthode de calcul :</strong> le MRR historique et projeté est reconstitué à partir des contrats existants
          (dates d&apos;installation, dates de fin, dates d&apos;effet de résiliation). Trois types de sorties sont pris en compte :
          <strong className="text-red-700"> résiliés</strong> (validés),
          <strong className="text-orange-700"> résiliations en cours</strong> (demandées non validées) et
          <strong className="text-gray-700"> fins de contrat</strong> (échéance naturelle atteinte).
          Aucune donnée n&apos;est extrapolée ou inventée.
        </p>
      </div>
    </div>
  );
}
